import React from 'react';
import { observer } from 'mobx-react';
import { useParams } from 'react-router-dom';

import { Link } from 'ui/base/link';
import { Theme, ThemedStyles, useStyles } from 'ui/base/theme';
import { BackButton } from 'ui/base/button/back_button';
import { StandardPage } from 'ui/base/page/standard_page/standard_page';

import { BasePath } from 'services/base/page_path';

import { Pangolin } from 'services/accounts/types';
import { useAccountsDomain } from 'services/accounts/accounts_domain';

import { IPangolinDomain } from 'services/pangolin/types';
import { usePangolinDomain } from 'services/pangolin/pangolin_domain';

import { Redirect } from 'pages/core/redirect/redirect';

import { ScheduleDisplay } from './schedule_display/schedule_display';

import baseStyles from './list_schedules.module.css';
import Messages from './list_schedules_messages';


type ListSchedulesStyles = {
  row: string;
  headerText: string;
  iconContainer: string;
  parentalControlIcon: string;
  content: string;
  contentRow: string;
  labelText: string;
  plusIcon: string;
  link: string;
};

const themedStyles: ThemedStyles<ListSchedulesStyles> = {
  [Theme.DAYLIGHT]: {
    row: baseStyles.baseRow,
    headerText: baseStyles.daylightHeaderText,
    iconContainer: baseStyles.baseIconContainer,
    parentalControlIcon: baseStyles.daylightParentalControlIcon,
    content: baseStyles.baseContent,
    contentRow: baseStyles.baseContentRow,
    labelText: baseStyles.daylightLabelText,
    plusIcon: baseStyles.daylightPlusIcon,
    link: baseStyles.daylightLink,
  },
  [Theme.MIDNIGHT]: {
    row: baseStyles.baseRow,
    headerText: baseStyles.midnightHeaderText,
    iconContainer: baseStyles.baseIconContainer,
    parentalControlIcon: baseStyles.midnightParentalControlIcon,
    content: baseStyles.baseContent,
    contentRow: baseStyles.baseContentRow,
    labelText: baseStyles.midnightLabelText,
    plusIcon: baseStyles.midnightPlusIcon,
    link: baseStyles.midnightLink,
  },
};

type BaseListSchedulesProps = {
  pangolin: Pangolin;
  domain: IPangolinDomain;
};
export const BaseListSchedules = observer(({ pangolin, domain }: BaseListSchedulesProps) => {
  const styles = useStyles(themedStyles);
  return (
    <StandardPage>
      <div className={styles.row}>
        <div><BackButton/></div>
        <div className={styles.headerText}>
          {Messages.parentalControls()}
        </div>
      </div>
      <div className={styles.iconContainer}>
        <div className={styles.parentalControlIcon}/>
      </div>
      <div className={styles.content}>
        <div className={styles.contentRow}>
          <div className={styles.labelText}>
            {Messages.activeRules()}
          </div>
          <Link path={{ kind: BasePath.SCHEDULE_ADD, pangolinID: pangolin.id }} className={styles.link}>
            <div className={styles.plusIcon}/>
          </Link>
        </div>
      </div>
      {domain.schedules.map((schedule) => (
        <ScheduleDisplay
          schedule={schedule}
          domain={domain}
          key={schedule.uuid}
        />
      ))}
    </StandardPage>
  );
});

export const ListSchedules = observer(() => {
  const { pangolinID } = useParams<{ pangolinID: string }>();
  if (!pangolinID) return <Redirect />;

  const accounts = useAccountsDomain();

  const pangolin = accounts.pangolins.get(pangolinID);
  if (!pangolin) return <Redirect />;

  const domain = usePangolinDomain(pangolin.id);
  if (!domain) return <Redirect />;

  return (
    <BaseListSchedules
      pangolin={pangolin}
      domain={domain}
    />
  );
});
